import { useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { Icon } from "@iconify/react";
import {
  AddVestingMilestoneReq,
  ClosureType,
  MilestoneDateType,
  MilestoneOffsetType,
  MilestoneType,
  MilestoneClassification,
} from "../../types/milestone";
import {
  useCreateMilestones,
  useUpdateMilestones,
} from "../../queries/milestone";
import {
  Box,
  HStack,
  VStack,
  Error,
  ButtonPrimary1,
  ButtonPrimary,
} from "../../components/utils";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";

type AddOrEditMilestoneProps = {
  mode: "Edit" | "Add" | "Delete";
  data?: AddVestingMilestoneReq;
  onClose: () => void;
};

function AddOrEditMilestone(props: AddOrEditMilestoneProps) {
  const { data, mode, onClose } = props;
  const baseValues: AddVestingMilestoneReq = {
    milestoneName: "",
    closureType: ClosureType.Manual,
    milestoneType: MilestoneType.CompanyWide,
    targetDateType: MilestoneDateType.Absolute,
    offsetType: MilestoneOffsetType.DAYS,
    notes: "",
  };

  const [description, setDescription] = useState(
    data?.metaData?.description || ""
  );
  const [classification, setClassification] = useState<MilestoneClassification>(
    data?.metaData?.classification || MilestoneClassification.EVENT
  );

  function getInitialValuesEditing(data: AddVestingMilestoneReq | undefined) {
    if (!data) return baseValues;
    return {
      id: data.id,
      milestoneName: data.milestoneName,
      closureType: data.closureType,
      milestoneType: data.milestoneType,
      targetDate: data.targetDate
        ? new Date(data.targetDate).toISOString().split("T")[0]
        : undefined,
      targetDateType: data.targetDate
        ? MilestoneDateType.Absolute
        : MilestoneDateType.Relative,
      vestingOffset: data.vestingOffset,
      offsetType: data.offsetType,
      notes: data.notes,
    };
  }

  function getInitialValues() {
    if (mode === "Edit") {
      return getInitialValuesEditing(data);
    } else {
      return baseValues;
    }
  }

  const { mutate: addMilestone, isLoading: addMilestoneloading } =
    useCreateMilestones();
  const { mutate: editMilestone, isLoading: editMilestoneloading } =
    useUpdateMilestones();

  const validationSchema = Yup.object().shape({
    milestoneName: Yup.string().required("name is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: () => {
      if (mode === "Add") {
        const milestoneDto: AddVestingMilestoneReq = {
          milestoneName: formik.values.milestoneName,
          closureType: formik.values.closureType,
          milestoneType: formik.values.milestoneType,
          targetDate: formik.values.targetDate
            ? new Date(formik.values.targetDate as any)
            : undefined,
          vestingOffset: formik.values.vestingOffset,
          offsetType: formik.values.offsetType,
          notes: formik.values.notes,
          metaData: {
            description: description || undefined,
            classification,
          },
        };
        addMilestone([milestoneDto], {
          onSuccess: () => {
            toast("Milestone Added Successfully!", { type: "success" });
            onClose();
          },
          onError: (err: any) => {
            toast(err.response.data.errorMessage, {
              type: "error",
              autoClose: 2000,
            });
          },
        });
      } else {
        editMilestone(
          {
            id: data?.id,
            milestoneName: formik.values.milestoneName,
            closureType: formik.values.closureType,
            milestoneType: formik.values.milestoneType,
            targetDate: formik.values.targetDate
              ? new Date(formik.values.targetDate as any)
              : undefined,
            vestingOffset: formik.values.vestingOffset,
            offsetType: formik.values.offsetType,
            notes: formik.values.notes,
            metaData: {
              description: description || undefined,
              classification,
            },
          },
          {
            onSuccess: () => {
              toast("Milestone Edited Successfully!", { type: "success" });
              onClose();
            },
            onError: (err: any) => {
              toast(err.response.data.errorMessage, {
                type: "error",
                autoClose: 2000,
              });
            },
          }
        );
      }
    },
  });

  return (
    <>
      <Box className="px-10 text-lg font-medium border-b py-7">
        <h6 className="flex justify-between">
          {mode === "Edit" ? "Edit Milestone" : "Add Milestone"}{" "}
          <span onClick={() => onClose()} className="cursor-pointer">
            <Icon icon="material-symbols:close-rounded" />
          </span>{" "}
        </h6>
      </Box>
      <VStack className="w-full px-10 py-7 gap-9">
        <HStack className="gap-8">
          <div className="flex-1">
            <Label className="required">Milestone Name</Label>
            <Input type="text" {...formik.getFieldProps("milestoneName")} />
            {formik.touched.milestoneName && formik.errors.milestoneName && (
              <Error text={formik.errors.milestoneName} />
            )}
          </div>
          <div className="flex-1">
            <Label>Milestone Type</Label>
            <Select
              options={[MilestoneType.CompanyWide, MilestoneType.Individual]}
              {...formik.getFieldProps("milestoneType")}
            />
            {formik.touched.milestoneType && formik.errors.milestoneType && (
              <Error text={formik.errors.milestoneType} />
            )}
          </div>
        </HStack>
        <HStack className="gap-8">
          <div className="flex-1">
            <Label>Closure Type</Label>
            <Select
              options={[ClosureType.Manual]}
              {...formik.getFieldProps("closureType")}
            />
            {formik.touched.closureType && formik.errors.closureType && (
              <Error text={formik.errors.closureType} />
            )}
          </div>
          <div className="flex-1">
            <Label>Target Date Type</Label>
            <Select
              options={["Absolute", "Relative"]}
              {...formik.getFieldProps("targetDateType")}
            />
            {formik.touched.targetDateType && formik.errors.targetDateType && (
              <Error text={formik.errors.targetDateType} />
            )}
          </div>
        </HStack>
        {formik.values.targetDateType?.toLocaleUpperCase() === "RELATIVE" ? (
          <>
            <HStack className="gap-8">
              <div className="flex-1">
                <Label>Offset Period</Label>
                <Input
                  type="number"
                  {...formik.getFieldProps("vestingOffset")}
                />
              </div>
              <div className="flex-1">
                <Label>Offset Type</Label>
                <Select
                  options={["DAYS", "MONTHS", "YEARS"]}
                  {...formik.getFieldProps("offsetType")}
                />
              </div>
            </HStack>
            <HStack className="gap-8">
              <div className="flex-1">
                <Label>Classification</Label>
                <Select
                  options={[
                    MilestoneClassification.EVENT,
                    MilestoneClassification.PERFORMANCE,
                    MilestoneClassification.MILESTONE,
                  ]}
                  value={classification}
                  textGetter={(option) =>
                    option.charAt(0).toUpperCase() +
                    option.slice(1).toLowerCase()
                  }
                  onChange={(e: any) =>
                    setClassification(e.target.value as MilestoneClassification)
                  }
                />
              </div>
              <div className="flex-1"></div>
            </HStack>
          </>
        ) : (
          <HStack className="gap-8">
            <div className="flex-1">
              <Label className="required">Target Date</Label>
              <Input type="date" {...formik.getFieldProps("targetDate")} />
              {formik.touched.targetDate && formik.errors.targetDate && (
                <Error text={formik.errors.targetDate} />
              )}
            </div>
            <div className="flex-1">
              <Label>Classification</Label>
              <Select
                options={[
                  MilestoneClassification.EVENT,
                  MilestoneClassification.PERFORMANCE,
                  MilestoneClassification.MILESTONE,
                ]}
                value={classification}
                textGetter={(option) =>
                  option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()
                }
                onChange={(e: any) =>
                  setClassification(e.target.value as MilestoneClassification)
                }
              />
            </div>
          </HStack>
        )}
        <HStack className="gap-8">
          <div className="flex-1">
            <Label className="text-sm font-normal">
              Description (Visible to employees)
            </Label>
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description for this milestone"
            />
          </div>
        </HStack>
        <HStack className="gap-8">
          <div className="flex-1">
            <Label className="text-sm font-normal">
              Notes (For admin reference only)
            </Label>
            <TextArea {...formik.getFieldProps("notes")} />
          </div>
        </HStack>
        <HStack className="justify-between">
          <ButtonPrimary1 onClick={() => onClose()}>Back</ButtonPrimary1>
          <ButtonPrimary
            type="submit"
            onClick={(e) => {
              formik.handleSubmit();
            }}
            loading={addMilestoneloading || editMilestoneloading}
          >
            {mode === "Add" ? "Create" : "Update"}
          </ButtonPrimary>
        </HStack>
      </VStack>
    </>
  );
}

export default AddOrEditMilestone;
